<template>
  <b-modal v-model="isActive" :width="480" scroll="keep">
    <div class="card">
      <div class="card-content">
        <h3 class="is-size-4 has-text-weight-bold mb-4">
          Transferir puntos por CSV
        </h3>
        <hr />

        <b-notification
          v-if="pointList.length === 0"
          type="is-warning"
          has-icon
          :closable="false"
        >
          No tienes bolsas de puntos disponibles para transferir.
        </b-notification>

        <section v-else>
          <b-field label="Bolsa de puntos">
            <b-select
              placeholder="Selecciona una bolsa"
              v-model="selectedBag"
              expanded
            >
              <option v-for="point in pointList" :key="point.id" :value="point">
                {{ point.points_available }} (vence:
                {{ formatDate(point.expiration_date) }})
              </option>
            </b-select>
          </b-field>

          <p class="mb-3">
            Sube un archivo CSV con dos columnas: primero el
            <strong>código</strong> del cliente y luego los
            <strong>puntos</strong> a transferirle.
          </p>
          <b-field>
            <b-upload
              v-model="file"
              accept=".csv"
              drag-drop
              expanded
              :disabled="!selectedBag"
              @input="parseErrorMessage = ''"
            >
              <section class="has-text-centered p-5">
                <p>
                  <font-awesome-icon :icon="['fas', 'file-csv']" size="2x" />
                </p>
                <p v-if="file">{{ file.name }}</p>
                <p v-else>
                  Arrastra tu archivo aquí o haz clic para seleccionarlo
                </p>
              </section>
            </b-upload>
          </b-field>
          <b-tag v-if="file" type="is-success" closable @close="file = null">
            {{ file.name }}
          </b-tag>
          <p v-if="parseErrorMessage" class="has-text-danger is-size-7 mt-2">
            {{ parseErrorMessage }}
          </p>
        </section>

        <hr />
        <div
          class="is-flex is-justify-content-flex-end w-full is-align-items-center"
        >
          <div>
            <Button variant="gray" @click.native="isActive = false">
              Cancelar
            </Button>
          </div>
          <div class="ml-3">
            <Button
              variant="main"
              :spinner="isParsing"
              :disabled="!file || !selectedBag || isParsing"
              @click.native="handleContinue"
            >
              Continuar
            </Button>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import Button from "@/components/form/Button";

export default {
  name: "ModalTransferCsv",
  components: {
    Button
  },
  props: {
    name: {
      type: String,
      default: ""
    },
    pointList: {
      type: Array,
      default: () => []
    },
    bagPointId: {
      type: [Number, String],
      default: 0
    }
  },
  data() {
    return {
      isActive: true,
      isParsing: false,
      file: null,
      parseErrorMessage: "",
      selectedBag: null
    };
  },
  watch: {
    isActive(newValue) {
      if (!newValue) {
        this.$emit("hidden");
      }
    }
  },
  created() {
    if (this.pointList.length > 0) {
      this.selectedBag =
        this.pointList.find(point => point.id === this.bagPointId) ||
        this.pointList[0];
    }
  },
  methods: {
    formatDate(fechaOriginal) {
      if (fechaOriginal) {
        const partes = fechaOriginal.split(" ");
        const fecha = partes[0];
        const partesFecha = fecha.split("-");
        const dia = partesFecha[0];
        const mes = partesFecha[1];
        const año = partesFecha[2];
        return `${mes}/${dia}/${año}`;
      }
      return "";
    },
    parseCsv(text) {
      const lines = text.split(/\r\n|\n|\r/).filter(line => line.trim() !== "");
      if (lines.length === 0) {
        throw new Error("El archivo está vacío.");
      }
      const delimiter =
        lines[0].includes(";") && !lines[0].includes(",") ? ";" : ",";
      const headerColumns = lines[0].split(delimiter);
      if (headerColumns.length !== 2) {
        throw new Error("El archivo debe tener dos columnas: codigo y points.");
      }
      const rows = [];
      const errors = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(delimiter);
        if (cols.length !== 2) {
          errors.push({
            line: i + 1,
            codigo: (cols[0] || "").trim(),
            pointsRaw: (cols[1] || "").trim()
          });
          continue;
        }
        const codigo = cols[0].trim();
        const pointsRaw = cols[1].trim();
        const points = Number(pointsRaw);
        if (!codigo || !pointsRaw || !Number.isInteger(points) || points <= 0) {
          errors.push({ line: i + 1, codigo, pointsRaw });
          continue;
        }
        rows.push({ codigo, points });
      }
      return { rows, errors };
    },
    handleContinue() {
      if (!this.file || !this.selectedBag) {
        return;
      }
      this.isParsing = true;
      this.parseErrorMessage = "";
      const reader = new FileReader();
      reader.onload = event => {
        try {
          const { rows, errors } = this.parseCsv(event.target.result);
          if (rows.length === 0) {
            this.parseErrorMessage =
              "No se encontraron filas válidas en el archivo.";
            return;
          }
          const totalPointsToTransfer = rows.reduce(
            (total, row) => total + row.points,
            0
          );
          if (totalPointsToTransfer > this.selectedBag.points_available) {
            this.parseErrorMessage =
              "No tienes suficientes puntos disponibles en la bolsa seleccionada para transferir lo que pide el archivo.";
            return;
          }
          this.$store.commit("pointsTransfer/SET_CSV_DRAFT", {
            file: this.file,
            rows,
            errors,
            bag: this.selectedBag,
            pointList: this.pointList,
            name: this.name
          });
          this.isActive = false;
          this.$router.push({ name: "puntos-weeare-stock-transfer-bulk" });
        } catch (error) {
          this.parseErrorMessage = error.message;
        } finally {
          this.isParsing = false;
        }
      };
      reader.onerror = () => {
        this.parseErrorMessage = "No se pudo leer el archivo.";
        this.isParsing = false;
      };
      reader.readAsText(this.file, "UTF-8");
    }
  }
};
</script>
